import type { ComponentType } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Image as ImageIcon, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { cn } from "@/lib/utils";

interface NewReportSheetProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Choice = {
  kind: "issues" | "records";
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  /** The kind's colour, as on the report rows. See [[collection-row.spec]]. */
  iconClassName: string;
};

// Issues first: Photos already has its own way into a report (Select on the
// Photos page), and issues are the newer, less discoverable kind.
const CHOICES: readonly Choice[] = [
  {
    kind: "issues",
    title: "Issues",
    description: "Punch-list items, with their status and updates.",
    icon: ListChecks,
    iconClassName: "text-kind-issues",
  },
  {
    kind: "records",
    title: "Photos",
    description: "Jobsite photos, laid out by template with remarks.",
    icon: ImageIcon,
    iconClassName: "text-kind-photos",
  },
];

/**
 * New Report's first question: what the report holds. A report holds issues or
 * photos for life, so the kind comes first and each answer goes straight to
 * its picker in report selection mode. A bottom sheet on a phone, a dialog on
 * desktop. See [[new-report-sheet.spec]].
 */
export function NewReportSheet({
  projectId,
  open,
  onOpenChange,
}: NewReportSheetProps) {
  const isDesktop = useIsDesktop();

  const isDialog = isDesktop;
  const Header = isDialog ? DialogHeader : DrawerHeader;
  const Title = isDialog ? DialogTitle : DrawerTitle;
  const Description = isDialog ? DialogDescription : DrawerDescription;

  const body = (
    <>
      <Header className={cn(isDialog ? "px-6 pt-6 pr-12 text-left" : "text-center")}>
        <Title>New report</Title>
        <Description className="text-sm text-muted-foreground">
          A report holds issues or photos, never both. You can&apos;t switch it
          later.
        </Description>
      </Header>
      <div className={cn("flex flex-col gap-2", isDialog ? "px-6 py-4" : "px-4 pb-4")}>
        {CHOICES.map(({ kind, title, description, icon: Icon, iconClassName }) => (
          <Item key={kind} variant="outline" asChild className="bg-card">
            {/* A link, because choosing navigates: each picker opens in report
                selection mode (`?isReport=true`). Pushed, so Back from the
                picker returns to Reports. */}
            <Link
              to={
                kind === "issues"
                  ? "/projects/$projectId/issues"
                  : "/projects/$projectId/records"
              }
              params={{ projectId }}
              search={{ isReport: true } as never}
              onClick={() => onOpenChange(false)}
            >
              <ItemMedia variant="icon">
                <Icon aria-hidden="true" className={iconClassName} />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>{title}</ItemTitle>
                <ItemDescription>{description}</ItemDescription>
              </ItemContent>
              <ItemActions>
                <ChevronRight
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
              </ItemActions>
            </Link>
          </Item>
        ))}
      </div>
      <div
        className={cn(
          "flex gap-2 border-t border-border",
          isDialog ? "justify-end px-6 py-4" : "pb-sheet-actions p-4",
        )}
      >
        <Button
          variant="outline"
          className={isDialog ? undefined : "flex-1"}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
      </div>
    </>
  );

  if (isDialog) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent bare className="sm:max-w-md">
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>{body}</DrawerContent>
    </Drawer>
  );
}
